// APConfirmDialog — the v4 confirm pattern (successor to v3 `Modal.confirm`;
// spec § Context non-stock gate: confirm compositions are build-own).
// Destructive actions get `destructive`; async `onConfirm` shows a pending
// spinner and keeps the dialog open until it settles.
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';
import { DangerTriangleLinear } from '@assessprep/icons/solar';

export interface APConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already-translated title. */
  title: ReactNode;
  /** Already-translated supporting copy (short form). */
  description?: ReactNode;
  /** Rich body content (long-form confirms — lists, warnings). */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in the destructive variant. */
  destructive?: boolean;
  /** Gate the confirm button (type-to-confirm patterns, missing prerequisites). */
  confirmDisabled?: boolean;
  /** Resolves → dialog closes; rejects/throws → dialog stays open. */
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function APConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  className,
  'data-testid': testId = 'confirm-dialog',
}: APConfirmDialogProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Stay open — the failure surfaces via Refine's notification provider.
    } finally {
      setPending(false);
    }
  };

  const headerText = (
    <>
      <DialogTitle>
        {/* text-lg restores the line-height DialogTitle's `leading-none` drops; without it wrapped lines overlap. */}
        <span className="min-w-0 text-lg break-words">{title}</span>
      </DialogTitle>
      {description && <DialogDescription>{description}</DialogDescription>}
    </>
  );

  // h-[1lh] with text-lg centres the icon on the FIRST title line at any reader line-height.
  const warningIcon = (
    // eslint-disable-next-line no-restricted-syntax -- `1lh` is a relative unit, the class the rule already allows (%, vw, calc)
    <span className="flex h-[1lh] shrink-0 items-center text-lg">
      <DangerTriangleLinear aria-hidden="true" className="size-6 text-destructive" />
    </span>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Esc / overlay dismissal counts as cancel.
        if (!nextOpen) onCancel?.();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className={cn('sm:max-w-md', className)} data-testid={testId}>
        <DialogHeader>
          {destructive ? (
            <div className="flex items-start gap-2">
              {warningIcon}
              {/* min-w-0 must sit on the flex item so a long unbreakable title wraps (CN7-50). */}
              <div className="flex min-w-0 flex-1 flex-col gap-2">{headerText}</div>
            </div>
          ) : (
            headerText
          )}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={pending}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel ?? t('CommonText.cancel')}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => void handleConfirm()}
            disabled={pending || confirmDisabled}
            data-testid={`${testId}-confirm`}
          >
            {pending && <Spinner aria-hidden="true" />}
            {confirmLabel ?? t('CommonText.yes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
